// 2026-04-24: DialogHost — render modal cho confirm/alert/prompt
// Mount 1 lần ở App root. Dialog state quản lý qua zustand store.
//
// UX:
//   - Backdrop click → cancel/close
//   - Esc → cancel/close
//   - Enter → confirm (cho confirm/alert; cho prompt nếu single-line input)
//   - Auto-focus vào input (prompt) hoặc nút confirm
//   - Trap focus đơn giản (không tab ra ngoài)

import { useEffect, useRef, useState } from 'react';
import { useDialogStore } from '../lib/dialogs.js';

const VARIANT_STYLES = {
  info:    { ring: 'border-sky-200 bg-sky-50',     icon: 'ℹ️', color: 'text-sky-700' },
  success: { ring: 'border-emerald-200 bg-emerald-50', icon: '✅', color: 'text-emerald-700' },
  warning: { ring: 'border-amber-200 bg-amber-50', icon: '⚠️', color: 'text-amber-700' },
  error:   { ring: 'border-rose-200 bg-rose-50',   icon: '❌', color: 'text-rose-700' },
};

export default function DialogHost() {
  const cur = useDialogStore((s) => s.current);
  if (!cur) return null;
  return <DialogModal key={Date.now()} spec={cur} />;
}

function DialogModal({ spec }) {
  const { type, title, message, confirmText, cancelText, dangerous } = spec;
  const [val, setVal] = useState(spec.defaultValue ?? '');
  const inputRef = useRef(null);
  const confirmBtnRef = useRef(null);

  // Auto-focus
  useEffect(() => {
    if (type === 'prompt') {
      const el = inputRef.current;
      if (el) {
        el.focus();
        // Select all để user gõ thay luôn
        if (typeof el.setSelectionRange === 'function') {
          el.setSelectionRange(0, el.value.length);
        }
      }
    } else {
      confirmBtnRef.current?.focus();
    }
  }, [type]);

  // Esc + Enter handler
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter') {
        // Enter trong textarea (multiline) → để default (xuống dòng)
        if (type === 'prompt' && spec.multiline) return;
        // Enter trong input — confirm
        if (e.target?.tagName?.toLowerCase() === 'input' || type !== 'prompt') {
          e.preventDefault();
          handleConfirm();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val, type]);

  function handleConfirm() {
    if (type === 'prompt') spec.resolve(val);
    else if (type === 'confirm') spec.resolve(true);
    else spec.resolve(undefined);  // alert
  }

  function handleCancel() {
    if (type === 'prompt') spec.resolve(null);
    else if (type === 'confirm') spec.resolve(false);
    else spec.resolve(undefined);  // alert (treat as ack)
  }

  // Backdrop click
  function onBackdropClick(e) {
    if (e.target === e.currentTarget) handleCancel();
  }

  const variantStyle = VARIANT_STYLES[spec.variant || 'info'];
  const isAlert = type === 'alert';
  const isConfirm = type === 'confirm';
  const isPrompt = type === 'prompt';

  // Title default theo type
  const effectiveTitle = title || (
    isAlert ? 'Thông báo' :
    isConfirm ? 'Xác nhận' :
    'Nhập thông tin'
  );

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[100] p-4 animate-[fadeIn_120ms_ease-out]"
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-[scaleIn_140ms_ease-out]"
        style={{ transformOrigin: 'center' }}
      >
        {/* Header */}
        <div className={`px-5 py-3 border-b ${
          isAlert ? variantStyle.ring : (
            dangerous ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50'
          )
        }`}>
          <h3 id="dialog-title" className={`font-semibold text-base ${
            isAlert ? variantStyle.color :
            dangerous ? 'text-rose-700' :
            'text-slate-800'
          }`}>
            {isAlert && <span className="mr-1.5">{variantStyle.icon}</span>}
            {dangerous && isConfirm && <span className="mr-1.5">⚠️</span>}
            {effectiveTitle}
          </h3>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {message && (
            <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {message}
            </div>
          )}

          {isPrompt && (
            <div className="mt-3">
              {spec.multiline ? (
                <textarea
                  ref={inputRef}
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder={spec.placeholder}
                  rows={4}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none resize-y"
                />
              ) : (
                <input
                  ref={inputRef}
                  type={spec.inputType || 'text'}
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder={spec.placeholder}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                />
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex gap-2 justify-end">
          {(isConfirm || isPrompt) && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-2 text-sm rounded-lg bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 font-medium"
            >
              {cancelText}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm rounded-lg font-medium text-white ${
              dangerous
                ? 'bg-rose-600 hover:bg-rose-700'
                : 'bg-brand-600 hover:bg-brand-700'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}
